import React, { useEffect, useState } from 'react'
import { CustomDropdownItemOption, DropDownItemOptionComponentProps, SelectDropdown, SelectOption } from '../../SelectDropdown/SelectDropdown'
import { InputParameterConfigForm } from '../InputParameterConfigForm'
import { YesNoCancelModal } from '../../Modal/YesNoCancelModal'
import { Button } from '../../Button/Button'
import { PresetConfigLoader } from '../../../views/Setup/AIFeaturePresets/PresetConfig/Common/PresetConfigLoader'
import { InviteTeamMembers } from '../../../views/Setup/AIFeaturePresets/PresetConfig/Common/InviteTeamMembers'
import { ScopedBlock } from '../../ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../ScopedBlock/models'
import { useMeetingTemplateEffectiveInputParameters, useInputParametersUpdateScope, useTeamMembersFilter, useUserInputParameterConfigForm } from '../hooks'
import { MeetingTemplateInputParametersHierarchyConfigComponentProps } from '../model'

export const MeetingTemplateTeamMemberInputParameterConfigForm: React.FC<MeetingTemplateInputParametersHierarchyConfigComponentProps> = ({
  type,
  typeId,
  inputParametersSchema,
  defaultInputParameters,
  meetingTemplateId,
  onHide,
  showCustomizationSetting,
  ipHierarchyEnabled,
  lockedByEnterprise,
}) => {
  const { userFilterValues, selectedUserFilterOption, setSelectedUserFilterOption, hasTeamMembers, teamMembersLoading } = useTeamMembersFilter()

  const { inputParameters, parentInputParameters, updateLoading, updateInputParameters, isCustomized } = useMeetingTemplateEffectiveInputParameters(
    type,
    typeId,
    defaultInputParameters,
    selectedUserFilterOption?.id,
    meetingTemplateId,
  )

  if (teamMembersLoading || !inputParameters || !parentInputParameters) {
    return <PresetConfigLoader />
  }

  if (!hasTeamMembers) {
    return <InviteTeamMembers />
  }

  return (
    <TeamMemberPresetConfigFormBody
      inputParametersSchema={inputParametersSchema}
      inputParameters={inputParameters}
      parentInputParameters={parentInputParameters}
      isCustomized={isCustomized}
      showCustomizationSetting={showCustomizationSetting}
      onSave={updateInputParameters}
      onHide={onHide}
      updateLoading={updateLoading}
      userFilterValues={userFilterValues}
      selectedUserFilterOption={selectedUserFilterOption}
      setSelectedUserFilterOption={setSelectedUserFilterOption}
      ipHierarchyEnabled={ipHierarchyEnabled}
      lockedByEnterprise={lockedByEnterprise}
    />
  )
}

interface TeamMemberPresetConfigFormBodyProps {
  inputParametersSchema: { [key: string]: any } | undefined
  inputParameters: { [key: string]: any }
  parentInputParameters: { [key: string]: any }
  showCustomizationSetting: boolean
  isCustomized: boolean
  updateLoading: boolean
  onSave: (inputParameters: { [key: string]: any }, isCustomized: boolean) => Promise<void>
  onHide: () => void
  userFilterValues: SelectOption[]
  selectedUserFilterOption: SelectOption | null
  setSelectedUserFilterOption: (newSelectedOption: SelectOption) => void
  ipHierarchyEnabled: boolean
  lockedByEnterprise?: boolean
}

const TeamMemberPresetConfigFormBody: React.FC<TeamMemberPresetConfigFormBodyProps> = ({
  inputParametersSchema,
  inputParameters,
  parentInputParameters,
  showCustomizationSetting,
  isCustomized,
  updateLoading,
  onSave,
  onHide,
  userFilterValues,
  selectedUserFilterOption,
  setSelectedUserFilterOption,
  ipHierarchyEnabled,
  lockedByEnterprise = false,
}) => {
  const { hasIpHierarchyUpdateScope } = useInputParametersUpdateScope(selectedUserFilterOption?.id)

  const {
    editedInputParameters,
    customized,
    isChanged,
    showConfirmation,
    setEditedInputParameters,
    setShowConfirmation,
    handleChangeCustomized,
    handleClickCancel,
    handleClickSave,
    handleClickClose,
    checkForChange,
  } = useUserInputParameterConfigForm(inputParameters, parentInputParameters, isCustomized, onSave, onHide)

  const [changeToUser, setChangeToUser] = useState<SelectOption | null>(null)

  const handleUserSelected = (selectOption: SelectOption) => {
    if (isChanged) {
      setShowConfirmation(true)
      setChangeToUser(selectOption)
    } else {
      setSelectedUserFilterOption(selectOption)
    }
    return !isChanged
  }

  useEffect(() => {
    if (!selectedUserFilterOption) {
      return
    }
    checkForChange()
  }, [checkForChange, selectedUserFilterOption])

  const TeamMemberDropdownItemOption: React.FC<DropDownItemOptionComponentProps> = ({ onClick, optionId, optionLabel }) => {
    const selectOption = { id: optionId, label: optionLabel }
    const handleClick = (e: React.MouseEvent<HTMLElement>) => {
      const shouldProceed = handleUserSelected(selectOption)
      if (shouldProceed) {
        onClick(e)
      }
    }
    return (
      <CustomDropdownItemOption role='button' onClick={handleClick}>
        {optionLabel}
      </CustomDropdownItemOption>
    )
  }

  return (
    <>
      <YesNoCancelModal
        show={showConfirmation}
        onHide={() => {
          setShowConfirmation(false)
          setChangeToUser(null)
        }}
        message={'Do you want to save your changes?'}
        onYes={async () => {
          await onSave(editedInputParameters, customized)
          if (changeToUser) {
            setSelectedUserFilterOption(changeToUser)
            setChangeToUser(null)
            setShowConfirmation(false)
          } else {
            onHide()
          }
        }}
        onNo={() => {
          setEditedInputParameters(inputParameters)
          if (changeToUser) {
            setSelectedUserFilterOption(changeToUser)
            setChangeToUser(null)
            setShowConfirmation(false)
          } else {
            onHide()
          }
        }}
      />
      <div className='d-flex flex-column gap-4'>
        <div>
          <div className='mb-1 paragraph-medium-extended'>Choose team member</div>
          <SelectDropdown
            placeholder='Select a team member'
            options={userFilterValues}
            onOptionSelected={handleUserSelected}
            initialSelectedOption={selectedUserFilterOption}
            searchable
            DropDownItemOptionComponent={TeamMemberDropdownItemOption}
          />
        </div>
        {selectedUserFilterOption ? (
          <InputParameterConfigForm
            inputParametersSchema={inputParametersSchema}
            inputParameters={editedInputParameters}
            showCustomizationSetting={showCustomizationSetting}
            customized={customized}
            onChange={setEditedInputParameters}
            onChangeCustomized={handleChangeCustomized}
            disabled={!ipHierarchyEnabled || !hasIpHierarchyUpdateScope || lockedByEnterprise}
          />
        ) : null}
      </div>
      <div className='w-100 d-flex justify-content-between zocks-mt-3'>
        <Button variant='borderless' onClick={handleClickClose}>
          Close
        </Button>
        {hasIpHierarchyUpdateScope && !lockedByEnterprise && (
          <div className='d-flex gap-2'>
            <>
              {ipHierarchyEnabled && (
                <Button variant='secondary' onClick={handleClickCancel} disabled={!isChanged} loading={updateLoading}>
                  Cancel
                </Button>
              )}
              <ScopedBlock
                missingScopeAction={MissingScopeAction.UPGRADE}
                quota={{
                  quota: 'ai.ipHierarchy.value',
                }}
              >
                <Button variant='primary' onClick={handleClickSave} disabled={ipHierarchyEnabled && !isChanged} loading={updateLoading}>
                  {ipHierarchyEnabled ? 'Save' : 'Get feature'}
                </Button>
              </ScopedBlock>
            </>
          </div>
        )}
      </div>
    </>
  )
}
